// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps, GridOffset, GridWrapper } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@werner/App/Components/Common/XngZone";
import { GeneralLinksWrapper } from "@werner/Overrides/Widgets/Footer/FooterContainer.style";
import { checkoutPages, constants, DivWrapper } from "@werner/XDL/constants";
import { color, hideElement, layoutContainer, xScreen } from "@werner/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
    // XNG-Changes
    footerWrapper?: InjectableCss;
    footerTopSection?: InjectableCss;
    sectionContainer?: InjectableCss;
    supportServiceWrapper?: InjectableCss;
    footerMiddleSection?: InjectableCss;
    leftMiddleSection?: InjectableCss;
    rightMiddleSection?: InjectableCss;
    bottomMiddleSection?: InjectableCss;
    footerBottomSection?: InjectableCss;
    appsAndCertificates?: InjectableCss;
    mobileSocialLinks?: InjectableCss;
}

export const footerContainerStyles: FooterContainerStyles = {
    footerWrapper: {
        css: css`
            display: flex;
            flex: 1;
            flex-direction: column;
            width: 100%;
        `,
    },
    footerTopSection: {
        css: css`
            display: flex;
            background: ${color("primary")};
            flex: 1;

            ${xScreen("lte", "sm")} {
                flex-direction: column;
            }
        `,
    },
    logoWrapper: {
        css: css`
            margin-right: 66px;
            width: 176px;
            height: 37px;
            & .logo-wrapper {
                width: 176px;
                height: 37px;
            }
            ${xScreen("lte", "md")} {
                margin-right: 0;
            }
        `,
    },
    sectionContainer: {
        css: css`
            display: flex;
            width: 100%;
            padding: 24px 0;
            align-items: center;
            justify-content: space-between;
            &.footer-top-section {
                justify-content: flex-start;
                flex-wrap: wrap;
                row-gap: 15px;
            }
            ${layoutContainer}
            ${xScreen("lte", "md")} {
                &.footer-top-section {
                    flex-direction: column;
                }
            }
            ${xScreen("lte", "sm")} {
                flex-direction: column;
                ${({ hasLinks }: any) => (hasLinks ? "&&&& { padding: 0;margin: 0 -16px; }" : "")}
            }
        `,
    },

    supportServiceWrapper: {
        css: css`
            flex: 1 0 auto;
            height: auto;
            min-height: 0;
            max-height: 100%;
            width: auto;
            min-width: 0;
            justify-content: space-between;
            align-items: baseline;
            max-width: 100%;
            display: flex;
            flex-wrap: wrap;
            gap: 20px;
            & > ${DivWrapper} {
                width: unset;
            }
        `,
    },
    footerMiddleSection: {
        css: css`
            display: flex;
            column-gap: 97px;
            ${xScreen("lte", "sm")} {
                flex-direction: column;
            }
        `,
    },
    leftMiddleSection: {
        css: css`
            display: flex;
            > div {
                display: flex;
                flex-direction: column;
                row-gap: 12px;
                max-width: 150px;
                width: 100%;
            }
            ${xScreen("lte", "md")} {
                // 7 columns grid
                width: calc(7 / 12 * 100%);
            }
            ${xScreen("lte", "sm")} {
                flex-direction: column;
                width: 100%;
            }
        `,
    },
    rightMiddleSection: {
        css: css`
            display: flex;
            flex-direction: column;
            align-self: flex-start;
            width: calc(5 / 12 * 100%);
            ${xScreen("lte", constants.footer.breakPoints.md)} {
                ${hideElement}
            }
        `,
    },
    bottomMiddleSection: {
        css: css`
            display: flex;
            justify-content: space-between;

            ${xScreen("lte", "sm")} {
                .bottom-middle-section-wrapper {
                    display: flex;
                    flex-direction: column;
                    row-gap: 16px;
                    margin: 0 auto;
                }
            }

            .bottom-middle-section-wrapper {
                display: flex;
                align-items: flex-end;
                justify-content: center;
            }

            > ${StyledWrapper}.bottom-middle-section-wrapper > div {
                display: flex;
                column-gap: 8px;
                align-items: center;
                justify-content: center;
            }

            > ${StyledWrapper}.bottom-middle-section-wrapper > div:first-child {
                justify-content: flex-start;
            }

            > ${StyledWrapper}.bottom-middle-section-wrapper > div:last-child {
                column-gap: 20px;
                justify-content: flex-end;
                ${xScreen("lte", "sm")} {
                    justify-content: center;
                }
            }
            // targeting the certificate image wrapper
            > ${StyledWrapper}.bottom-middle-section-wrapper > div:last-child div {
                width: 50px;
            }
        `,
    },

    mobileSocialLinks: {
        css: css`
            ${hideElement}
            ${xScreen("lte", constants.footer.breakPoints.md)} {
                &&&& {
                    display: flex;
                    flex-direction: column;
                    row-gap: 24px;
                    width: 100%;
                    justify-content: flex-start;
                    flex-wrap: wrap;
                }
            }
        `,
    },
};

// XNG-Change: change layout
const FooterContainer: FC<OwnProps> = ({ id, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));
    // <XNG-Changes>
    const { isCheckoutPage } = useAppSelector(state => ({
        isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type),
    }));
    // </XNG-Changes>

    return (
        <StyledWrapper {...{ isCheckoutPage }} {...styles.footerWrapper}>
            <StyledWrapper {...styles.footerTopSection}>
                <StyledWrapper {...styles.sectionContainer} className="footer-top-section">
                    <StyledWrapper {...styles.logoWrapper} className="logo-wrapper">
                        <XngZone hint="Logo" contentId={id} zoneName="Logo" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.supportServiceWrapper}>
                        <XngZone hint="Zone for Support Location" zoneName="Location" contentId={id} />
                        <XngZone hint="Zone for Questions" zoneName="Questions" contentId={id} />
                        <XngZone hint="Zone for Hours" zoneName="WorkingHours" contentId={id} />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.footerMiddleSection}>
                <StyledWrapper {...styles.sectionContainer} {...{ hasLinks: true }}>
                    <StyledWrapper {...styles.leftMiddleSection}>
                        <GeneralLinksWrapper>
                            <XngZone hint="Zone for Links (1)" zoneName="LinksList1" contentId={id} />
                        </GeneralLinksWrapper>
                        <GeneralLinksWrapper>
                            <XngZone hint="Zone for Links (2)" zoneName="LinksList2" contentId={id} />
                        </GeneralLinksWrapper>
                        <GeneralLinksWrapper>
                            <XngZone hint="Zone for Links (3)" zoneName="LinksList3" contentId={id} />
                        </GeneralLinksWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.rightMiddleSection}>
                        <XngZone hint="Zone for LinkedIn Feeds" zoneName="LinkinFeeds" contentId={id} />
                        <XngZone hint="Zone for Social Links" zoneName="SocialLinks" contentId={id} />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.bottomMiddleSection}>
                <StyledWrapper {...styles.sectionContainer} className="bottom-middle-section-wrapper">
                    <XngZone hint="Zone for Subscribe Button" zoneName="SubscribeNow" contentId={id} />
                    <StyledWrapper {...styles.mobileSocialLinks}>
                        <XngZone hint="Zone for LinkedIn Feeds" zoneName="LinkinFeeds" contentId={id} />
                        <XngZone hint="Zone for Social Links" zoneName="SocialLinks" contentId={id} />
                    </StyledWrapper>
                    <XngZone hint="Zone for App Stores" zoneName="AppStores" contentId={id} />
                    <XngZone hint="Zone for Certificates" zoneName="Certificates" contentId={id} />
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.footerBottomSection}>
                <XngZone hint="Zone for Bottom Section" zoneName="Bottom Section" contentId={id} />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
